import { Crown, GraduationCap, HeartHandshake, MapPin, Rocket, Sparkle, type LucideIcon } from 'lucide-react';
import { aboutCards, portrait, stats } from '@/lib/portfolio';
import { cn } from '@/lib/utils';
import { Reveal } from './Reveal';
import { SectionHeading } from './SectionHeading';

const iconMap: Record<string, LucideIcon> = {
  GraduationCap,
  Crown,
  Rocket,
  HeartHandshake,
};

function PortraitCard() {
  return (
    <div className="group relative mx-auto max-w-md lg:max-w-none">
      {/* Aura */}
      <div
        className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-nova/30 via-pulsar/25 to-comet/20 opacity-70 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
        aria-hidden="true"
      />

      {/* Frame */}
      <div className="relative -rotate-2 overflow-hidden rounded-[2rem] border border-white/15 shadow-2xl transition-transform duration-500 group-hover:rotate-0">
        <div className="aspect-[4/5] overflow-hidden bg-card">
          <img
            src={portrait.src}
            alt={portrait.alt}
            loading="lazy"
            className="size-full object-cover object-[50%_22%] transition-transform duration-700 group-hover:scale-105"
          />
        </div>

        {/* Caption */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-5 pt-14">
          <p className="font-serif text-xl italic text-white">{portrait.caption}</p>
          <p className="mt-1 flex items-center gap-1.5 text-xs uppercase tracking-[0.25em] text-white/70">
            <MapPin className="size-3" />
            Nairobi, Kenya
          </p>
        </div>

        {/* Stickers */}
        <span className="absolute left-4 top-4 -rotate-6 rounded-full bg-nova px-3.5 py-1.5 font-display text-xs font-bold uppercase tracking-wider text-primary-foreground shadow-lg">
          CTO @ 20
        </span>
        <span className="absolute right-4 top-6 rotate-6 rounded-full bg-flare px-3.5 py-1.5 font-display text-xs font-bold uppercase tracking-wider text-primary-foreground shadow-lg">
          Est. 2006
        </span>
      </div>

      <Sparkle
        className="absolute -right-3 -top-3 size-7 animate-float text-flare"
        aria-hidden="true"
      />
    </div>
  );
}

export function About() {
  return (
    <section id="about" className="relative scroll-mt-28 py-28 md:py-36">
      <div className="container">
        <div className="grid items-center gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
          <Reveal>
            <PortraitCard />
          </Reveal>

          <div>
            <SectionHeading
              eyebrow="The origin story"
              title={
                <>
                  Math brain.{' '}
                  <span className="font-serif font-normal italic text-pulsar">Founder energy.</span>
                </>
              }
            />
            <Reveal delay={0.1} className="mt-8 space-y-5 text-lg leading-relaxed text-muted-foreground">
              <p>
                Immaculate Munde is a twenty-year-old builder from Nairobi who treats
                &ldquo;that&rsquo;s impossible&rdquo; as a starting condition, not a constraint.
                By day she studies Mathematics &amp; Computer Science at JKUAT; by night
                (and, honestly, also by day) she&rsquo;s the Co-founder &amp; CTO of{' '}
                <span className="font-semibold text-foreground">MissFit Tech</span>, teaching
                AI how clothes actually fit.
              </p>
              <p>
                In twelve months she went from her first hackathon to shipping escrow rails on
                Bitcoin Lightning, knowledge assistants on Avalanche, delivery verification on
                Stellar &mdash; and production platforms for real clients with real users.
                Next stop: the{' '}
                <span className="font-semibold text-foreground">Safaricom Technology Track</span>,
                October 2026.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Cards row */}
        <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {aboutCards.map((card, i) => {
            const Icon = iconMap[card.icon] ?? Rocket;
            return (
              <Reveal key={card.title} delay={0.06 * i}>
                <div className="group h-full rounded-3xl border border-white/10 bg-card/60 p-6 backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-white/25 hover:bg-card">
                  <span className="flex size-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 transition-transform duration-300 group-hover:scale-110">
                    <Icon className={cn('size-5', card.accent)} />
                  </span>
                  <h3 className="mt-4 font-display text-base font-semibold">{card.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{card.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Stats band */}
        <Reveal delay={0.1} className="mt-16">
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 md:grid-cols-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="group bg-card/80 p-6 backdrop-blur transition-colors hover:bg-card md:p-8"
              >
                <div className="font-display text-4xl font-extrabold tracking-tight text-foreground transition-colors group-hover:text-nova md:text-5xl">
                  {stat.value}
                </div>
                <div className="mt-2 text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
